import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { apiService } from '../services/apiService';

const LeaderboardsContainer = styled.div`
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 2rem;
`;

const Title = styled.h1`
    color: #2c3e50;
    margin-bottom: 0.5rem;
`;

const WeekInfo = styled.p`
    color: #7f8c8d;
    font-size: 1rem;
    margin: 0;
`;

const LeaderboardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const LeaderboardCard = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;
`;

const LeaderboardHeader = styled.div`
    background: ${props => props.color || '#3498db'};
    color: white;
    padding: 1rem;
    text-align: center;
`;

const LeaderboardTitle = styled.h2`
    margin: 0;
    font-size: 1.2rem;
`;

const LeaderboardSubtitle = styled.p`
    margin: 0.5rem 0 0 0;
    font-size: 0.9rem;
    opacity: 0.9;
`;

const LeaderboardList = styled.div`
    padding: 0;
`;

const LeaderboardItem = styled.div`
    display: flex;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid #ecf0f1;
    background: ${props => props.isCurrentUser ? '#f8f9fa' : 'white'};
    
    &:last-child {
        border-bottom: none;
    }
`;

const RankContainer = styled.div`
    position: relative;
    margin-right: 1rem;
    min-width: 40px;
    height: 50px; /* Increased height to accommodate crown */
    display: flex;
    align-items: flex-end;
`;

const Crown = styled.div`
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 24px;
    z-index: 2;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
`;

const Rank = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${props => {
        if (props.rank === 1) return '#f1c40f';
        if (props.rank === 2) return '#95a5a6';
        if (props.rank === 3) return '#cd7f32';
        return '#bdc3c7';
    }};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    position: relative;
    z-index: 1;
`;

const UserInfo = styled.div`
    flex: 1;
`;

const UserName = styled.div`
    font-weight: 600;
    color: #2c3e50;
    ${props => props.isCurrentUser && 'font-weight: bold;'}
`;

const Score = styled.div`
    font-size: 1.2rem;
    font-weight: bold;
    color: #2c3e50;
    text-align: right;
    min-width: 60px;
`;

const ScoreUnit = styled.span`
    font-size: 0.8rem;
    color: #7f8c8d;
    margin-left: 0.25rem;
`;

const ScoreDetails = styled.div`
    text-align: right;
    min-width: 80px;
`;

const MainScore = styled.div`
    font-size: 1.2rem;
    font-weight: bold;
    color: #2c3e50;
`;

const SubScore = styled.div`
    font-size: 0.75rem;
    color: #7f8c8d;
    margin-top: 2px;
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 3rem;
    color: #7f8c8d;
    font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
    background: #fee;
    color: #c0392b;
    padding: 1rem;
    border-radius: 4px;
    margin: 1rem 0;
    border-left: 4px solid #e74c3c;
    text-align: center;
`;

const EmptyMessage = styled.div`
    text-align: center;
    padding: 2rem;
    color: #7f8c8d;
    font-style: italic;
`;

function Leaderboards({ currentUser }) {
    const [leaderboardData, setLeaderboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const loadLeaderboards = async () => {
        setIsLoading(true);
        setError('');
        
        const result = await apiService.getLeaderboards();
        if (result.success) {
            setLeaderboardData(result.data);
        } else {
            setError(result.error);
            toast.error(result.error);
        }
        
        setIsLoading(false);
    };

    useEffect(() => {
        loadLeaderboards();
        
        // Auto-refresh every 1 minute
        const interval = setInterval(loadLeaderboards, 1 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const formatDateRange = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const options = { month: 'short', day: 'numeric' };
        
        if (start.getMonth() === end.getMonth()) {
            return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}-${end.getDate()}`;
        } else {
            return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
        }
    };

    const renderLeaderboard = (leaderboard, title, subtitle, color, scoreUnit, showAnnotations = false) => (
        <LeaderboardCard>
            <LeaderboardHeader color={color}>
                <LeaderboardTitle>{title}</LeaderboardTitle>
                <LeaderboardSubtitle>{subtitle}</LeaderboardSubtitle>
            </LeaderboardHeader>
            <LeaderboardList>
                {leaderboard.length === 0 ? (
                    <EmptyMessage>
                        No activity this week yet.<br />
                        Be the first to participate!
                    </EmptyMessage>
                ) : (
                    leaderboard.map((entry) => (
                        <LeaderboardItem 
                            key={entry.username}
                            isCurrentUser={currentUser && entry.username === currentUser.username}
                        >
                            <RankContainer>
                                {entry.rank === 1 && (
                                    <Crown>👑</Crown>
                                )}
                                <Rank rank={entry.rank}>
                                    {entry.rank}
                                </Rank>
                            </RankContainer>
                            <UserInfo>
                                <UserName isCurrentUser={currentUser && entry.username === currentUser.username}>
                                    {entry.display_name}
                                </UserName>
                            </UserInfo>
                            {showAnnotations && entry.annotations !== undefined ? (
                                <ScoreDetails>
                                    <MainScore>
                                        {entry.score}
                                        <ScoreUnit>{scoreUnit}</ScoreUnit>
                                    </MainScore>
                                    <SubScore>{entry.annotations} annotations</SubScore>
                                </ScoreDetails>
                            ) : (
                                <Score>
                                    {entry.score}
                                    <ScoreUnit>{scoreUnit}</ScoreUnit>
                                </Score>
                            )}
                        </LeaderboardItem>
                    ))
                )}
            </LeaderboardList>
        </LeaderboardCard>
    );

    if (isLoading) {
        return (
            <LeaderboardsContainer>
                <LoadingMessage>Loading leaderboards...</LoadingMessage>
            </LeaderboardsContainer>
        );
    }

    if (error) {
        return (
            <LeaderboardsContainer>
                <ErrorMessage>{error}</ErrorMessage>
            </LeaderboardsContainer>
        );
    }

    if (!leaderboardData) {
        return (
            <LeaderboardsContainer>
                <ErrorMessage>No leaderboard data available</ErrorMessage>
            </LeaderboardsContainer>
        );
    }

    const weekRange = formatDateRange(leaderboardData.week_start, leaderboardData.week_end);

    return (
        <LeaderboardsContainer>
            <Header>
                <Title>🏆 Weekly Leaderboards</Title>
                <WeekInfo>Week of {weekRange} • Resets every Monday</WeekInfo>
            </Header>
            
            <LeaderboardsGrid>
                {renderLeaderboard(
                    leaderboardData.login_streak_leaderboard,
                    "🔥 Login Streak",
                    "Consecutive days logged in this week",
                    "#e74c3c",
                    "days",
                    false
                )}
                
                {renderLeaderboard(
                    leaderboardData.activity_leaderboard,
                    "⚡ Most Active",
                    "Searches completed this week (across all dates)",
                    "#27ae60",
                    "searches",
                    true
                )}
            </LeaderboardsGrid>
        </LeaderboardsContainer>
    );
}

export default Leaderboards; 